import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Icon } from '@tviklax/ui-components';
import { Insight } from './types';

const Card = styled.div<{ $hasInsights: boolean }>`
  background: ${props => props.$hasInsights
    ? `linear-gradient(135deg, #f05a00 0%, #ff8c42 100%)` /* Slightly less saturated primary */
    : props.theme.colors.gray100};
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: ${props => props.theme.shadows.md};
  color: ${props => props.$hasInsights ? 'white' : props.theme.colors.gray500};
  min-height: 140px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all 0.5s ease-in-out;
`;

const Header = styled.div<{ $hasInsights: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ImpactTag = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

const Title = styled.h3<{ $hasInsights: boolean }>`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${props => props.$hasInsights ? 'white' : props.theme.colors.gray600};
`;

const InsightContent = styled(motion.div)`
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
  flex: 1;
`;

const ProgressDots = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 16px;
`;

const Dot = styled.div<{ $active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: white;
  opacity: ${props => props.$active ? 1 : 0.3};
  transition: opacity 0.3s;
`;

const Sparkle = styled(Icon)`
  position: absolute;
  top: -10px;
  right: -10px;
  opacity: 0.1;
  width: 100px;
  height: 100px;
`;

interface SmartInsightsProps {
  insights: Insight[];
}

export const SmartInsights: React.FC<SmartInsightsProps> = ({ insights }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (insights.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % insights.length);
    }, 8000);

    return () => clearInterval(timer);
  }, [insights.length]);

  const hasInsights = insights.length > 0;
  const currentInsight = insights[currentIndex];

  return (
    <Card $hasInsights={hasInsights}>
      <Sparkle name="Zap" />
      <Header $hasInsights={hasInsights}>
        <TitleContainer>
          <Icon name="Zap" color={hasInsights ? "white" : "#64748b"} size={20} />
          <Title $hasInsights={hasInsights}>Smart Insights</Title>
        </TitleContainer>
        {hasInsights && <ImpactTag>{currentInsight.impact}</ImpactTag>}
      </Header>

      {hasInsights && currentInsight && (
        <AnimatePresence mode="wait">
          <InsightContent
            key={currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4 }}
          >
            <div>"{currentInsight.text}"</div>
            {currentInsight.shipments && (
              <div style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.9 }}>
                Likely to affect {currentInsight.shipments} shipments
              </div>
            )}
            <Button size="sm" style={{ background: 'white', color: '#f05a00', marginTop: '12px', alignSelf: 'flex-start' }}>View Recommendation</Button>
          </InsightContent>
        </AnimatePresence>
      )}
      {!hasInsights && (
        <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>
          No insights available at this time
        </div>
      )}

      <ProgressDots>
        {insights.map((_, i) => (
          <Dot key={i} $active={i === currentIndex} />
        ))}
      </ProgressDots>
    </Card>
  );
};
